import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageSquare, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const [f, setF] = useState({ name: '', email: '', topic: '', message: '' });
  const { toast } = useToast();

  const submit = (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) {
      return toast({ title: 'Please complete all required fields' });
    }
    toast({ title: 'Message sent!', description: 'We\'ll get back to you within 2 working days.' });
    setF({ name: '', email: '', topic: '', message: '' });
  };

  const channels = [
    { icon: Phone, title: 'Call us', text: '0113 548 7557', href: 'tel:01135487557', sub: 'Monday–Friday, 9am–5pm', tint: 'bg-[#FFEDD6]' },
    { icon: Mail, title: 'Email us', text: 'hello@domestichomeservices.com', href: 'mailto:hello@domestichomeservices.com', sub: 'Replies within 2 working days', tint: 'bg-[#FCE0EC]' },
    { icon: MapPin, title: 'Registered Office', text: '128 City Road, London EC1V 2NX', sub: 'By appointment only', tint: 'bg-[#FFE0D6]' },
  ];

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main>
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-5 text-center">
            <div className="inline-flex items-center gap-2 bg-white border border-[#1F0068]/10 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#1F0068] mb-4">
              <Clock className="w-3.5 h-3.5" /> We reply within 2 working days
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-[#1F0068] tracking-tight leading-[1.05]">
              Let’s talk. <span className="text-[#FF5C39]">We’re here to help.</span>
            </h1>
            <p className="mt-4 text-lg text-[#1F0068]/70 max-w-2xl mx-auto">
              Questions about a plan, need to make a claim, or want to send feedback? Reach us any of these ways.
            </p>
          </div>
        </section>

        <section className="pb-10">
          <div className="max-w-6xl mx-auto px-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {channels.map((c) => {
              const Ico = c.icon;
              const Wrapper = c.href ? 'a' : 'div';
              const wrapperProps = c.href ? { href: c.href } : {};
              return (
                <Wrapper key={c.title} {...wrapperProps} className="dhs-card bg-white rounded-2xl p-6 border border-[#1F0068]/5 block hover:border-[#FF5C39]/40 transition-colors">
                  <div className={`w-11 h-11 rounded-xl ${c.tint} flex items-center justify-center text-[#1F0068] mb-4`}><Ico className="w-5 h-5" /></div>
                  <div className="font-bold text-[#1F0068]">{c.title}</div>
                  <div className="mt-1 text-sm text-[#1F0068] font-semibold">{c.text}</div>
                  <div className="text-xs text-[#1F0068]/60 mt-1">{c.sub}</div>
                </Wrapper>
              );
            })}
          </div>
        </section>

        <section className="py-10 md:py-16">
          <div className="max-w-3xl mx-auto px-5">
            <form onSubmit={submit} className="bg-white rounded-3xl p-6 md:p-10 border border-[#1F0068]/5 shadow-lg">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1F0068] tracking-tight">Send us a message</h2>
              <p className="text-sm text-[#1F0068]/70 mt-1">We’ll reply as soon as possible.</p>
              <div className="mt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Your name</Label>
                    <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Jane Smith" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Email</Label>
                    <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Topic</Label>
                  <Select value={f.topic} onValueChange={(v) => setF({ ...f, topic: v })}>
                    <SelectTrigger className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                    <SelectContent>
                      {['General enquiry', 'New quote', 'Claim question', 'Complaint', 'Feedback'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#1F0068] font-semibold">Message</Label>
                  <Textarea rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
                    placeholder="Tell us how we can help."
                    className="mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-[#1F0068]" />
                </div>
                <div className="text-right pt-2">
                  <Button type="submit" className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white h-12 px-7 gap-1.5 font-semibold">Send message <Send className="w-4 h-4" /></Button>
                </div>
              </div>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
